import { Patient } from '@chiroup/core/types/Patient.type';
import { ObjectiveParts, Visit } from '@chiroup/core/types/PatientVisit.type';
import React, { useEffect, useState } from 'react';
import UnifiedObjectiveStep from '../../../../patients/detail/visits/notes/UnifiedObjectiveStep';
import DiscoveryPopup from '../components/DiscoveryPopup';
import WalkthroughCallout from '../components/WalkthroughCallout';
import { SANDBOX_PATIENT } from '../fixtures/anitaBack';
import { DISCOVERY_COPY, OAP_COPY, OAP_SKIP_TO_PLAN } from '../sandboxCopy';
import { OapSection } from '../sandboxWalkthrough';
import PlanStep from './PlanStep';

interface OapStepProps {
  oapSection: OapSection;
  visitForm: Visit;
  setVisitForm: React.Dispatch<React.SetStateAction<Visit>>;
  onAdvance: () => void;
  /** Returns to the immediately preceding beat (requirement 2026-09-28). */
  onBack: () => void;
  /** Moves the walkthrough to a specific OAP beat without advancing linearly
   *  (tester note #15: desktop module clicks re-sync the panel, and every
   *  Objective box offers "Skip to Plan"). */
  onJumpToSection: (section: OapSection) => void;
}

const SECTION_TO_PART: Partial<Record<OapSection, ObjectiveParts>> = {
  joints: ObjectiveParts.Joints,
  rangeOfMotion: ObjectiveParts.RangeOfMotion,
  myofascial: ObjectiveParts.Myofascial,
  orthopedic: ObjectiveParts.Orthopedic,
};

const PART_TO_SECTION: Partial<Record<ObjectiveParts, OapSection>> = {
  [ObjectiveParts.Joints]: 'joints',
  [ObjectiveParts.RangeOfMotion]: 'rangeOfMotion',
  [ObjectiveParts.Myofascial]: 'myofascial',
  [ObjectiveParts.Orthopedic]: 'orthopedic',
};

type ObjectiveDiscovery =
  | 'neurologic'
  | 'jointsOtherRegions'
  | 'romOtherRegions'
  | 'addMuscles'
  | 'orthoTutorial'
  | 'orthoDetails'
  | 'addTests';

/**
 * Step 3 — Finalize OAP. The objective beats reuse the REAL
 * UnifiedObjectiveStep (fed by sandbox state instead of a live encounter), so
 * Accept Suggestions, laterality, sliders, and the Joints → CMT plan sync are
 * the genuine EHR behaviors. Each walkthrough beat remounts it with the
 * matching section open; the Plan beats hand off to the sandbox PlanStep.
 *
 * Discovery/hidden controls (spec §UI Element Classes) are applied here: Leg
 * Length Inequality and Remove region are hidden via component props;
 * Neurologic, other-region tabs, Add Muscles, test links, and Add Evaluations
 * are intercepted into informational pop-ups. The test-name links MUST be
 * intercepted regardless — they route to a nested test-detail view that
 * doesn't exist under /soap-sandbox and would exit the walkthrough.
 */
const OapStep: React.FC<OapStepProps> = ({
  oapSection,
  visitForm,
  setVisitForm,
  onAdvance,
  onBack,
  onJumpToSection,
}) => {
  const [discovery, setDiscovery] = useState<ObjectiveDiscovery | null>(null);

  // Each beat opens at its top (tester note #30): the linear mobile flow
  // otherwise inherits the previous module's scroll position, which can land
  // past the pane entirely.
  useEffect(() => {
    if (!window.matchMedia('(min-width: 1280px)').matches) {
      window.scrollTo({ top: 0 });
    }
  }, [oapSection]);

  if (oapSection === 'plan') {
    return (
      <PlanStep
        visitForm={visitForm}
        setVisitForm={setVisitForm}
        onAdvance={onAdvance}
        onBack={onBack}
      />
    );
  }

  const copy = OAP_COPY[oapSection];
  const complaints = visitForm.encounterInfo?.complaints || [];

  // Capture-phase interception for controls that live deep inside the reused
  // EHR components. Matching is intentionally conservative: exact tab labels,
  // anchors, and the add-muscle/add-evaluation inputs; everything else passes
  // through untouched.
  const interceptExploration = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    const swallow = (popup: ObjectiveDiscovery) => {
      e.preventDefault();
      e.stopPropagation();
      setDiscovery(popup);
    };

    // Orthopedic test-name links (react-router <Link> to a nested detail
    // route) — show the tutorial pop-up instead of navigating away.
    if (target.closest('a')) {
      swallow('orthoTutorial');

      return;
    }

    // Region switching, Add Muscles, and Add Evaluations are intercepted
    // inside their own components (interceptRegionSwitch / interceptAddMuscles
    // / interceptAddTests) — blanket label/input matching here swallowed
    // unrelated controls (tester notes #31, region-consistency note).
  };

  const discoveryContent: Record<
    ObjectiveDiscovery,
    { title: string; body: string; actionHint?: string }
  > = {
    neurologic: DISCOVERY_COPY.neurologic,
    jointsOtherRegions: DISCOVERY_COPY.jointsOtherRegions,
    romOtherRegions: DISCOVERY_COPY.romOtherRegions,
    addMuscles: DISCOVERY_COPY.addMuscles,
    orthoTutorial: {
      title: DISCOVERY_COPY.orthoTutorial.title,
      body: DISCOVERY_COPY.orthoTutorial.body,
      actionHint: DISCOVERY_COPY.orthoTutorial.cta,
    },
    orthoDetails: DISCOVERY_COPY.orthoDetails,
    addTests: DISCOVERY_COPY.addTests,
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Below xl the objective renders as a single stacked accordion, so the
          walkthrough panel lives above it in normal flow; at xl+ it docks
          beneath the module labels via desktopAside (tester note #12 — the
          old floating overlay covered the spine picker). */}
      {/* No Skip to Plan here (round-2 tester note #11): below xl the flow is
          linear with no module jumping, so the escape hatch is desktop-only. */}
      <div className="xl:hidden">
        <WalkthroughCallout
          stepLabel={copy.stepLabel}
          title={copy.title}
          body={copy.body}
          cta={copy.cta}
          onCta={onAdvance}
          onBack={onBack}
        />
      </div>

      <div
        // The max-sm override enlarges the spine picker on phones without
        // touching the shared Spine component (round-2 tester note #10): its
        // ~1:3 aspect ratio makes the stock 65vh canvas only ~165px wide
        // there. Scoped by targeting the canvas's own height utility class.
        className="rounded-lg border border-gray-200 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 max-sm:[&_.h-\[65vh\]]:h-[78vh]"
        onClickCapture={interceptExploration}
      >
        <UnifiedObjectiveStep
          // Remount per beat so the walkthrough always opens on its section
          // while leaving the user free to explore within the beat.
          key={oapSection}
          initialPartOpen={SECTION_TO_PART[oapSection]}
          // Below xl the master page (module label accordion) is eliminated:
          // each beat renders only its own working pane inline, in sequence
          // (tester note #30; supersedes note #27's modal auto-open).
          soloPartBelowXl
          interceptPartOpen={(part, surface) => {
            // Spec: Neurologic never opens another interface in the sandbox.
            if (part === ObjectiveParts.Neurological) {
              setDiscovery('neurologic');

              return true;
            }

            // Desktop module clicks re-sync the walkthrough beat (tester note
            // #15); the jump remounts this component (key) with the clicked
            // section open, so the open request itself is swallowed. Mobile
            // keeps its own accordion flow untouched — a jump's remount would
            // wipe the tapped section's open state before it ever showed.
            const section = PART_TO_SECTION[part];
            if (surface === 'desktop' && section && section !== oapSection) {
              onJumpToSection(section);

              return true;
            }

            return false;
          }}
          hideRegionControls
          hideCarryForward
          // The designations/contraindications system needs a real patient and
          // the EHR entitlement; without this the sandbox's spine sticks on
          // "Loading spine settings…" and can't be clicked (Kara, 2026-10-06).
          disableSpineDesignations
          interceptOrthoDetails={() => setDiscovery('orthoDetails')}
          interceptAddMuscles={() => setDiscovery('addMuscles')}
          interceptAddTests={() => setDiscovery('addTests')}
          interceptRegionSwitch={part =>
            setDiscovery(
              part === ObjectiveParts.RangeOfMotion ? 'romOtherRegions' : 'jointsOtherRegions'
            )
          }
          visitForm={visitForm}
          setVisitForm={setVisitForm}
          signed={false}
          complaints={complaints}
          isSaving={false}
          // UnifiedObjectiveStep accepts but never reads the patient row.
          patient={SANDBOX_PATIENT as unknown as Patient}
          readOnly={false}
          desktopAside={
            <WalkthroughCallout
              stepLabel={copy.stepLabel}
              title={copy.title}
              body={copy.body}
              cta={copy.cta}
              onCta={onAdvance}
              onBack={onBack}
              secondary={OAP_SKIP_TO_PLAN}
              onSecondary={() => onJumpToSection('plan')}
            />
          }
        />
      </div>

      {discovery && (
        <DiscoveryPopup
          title={discoveryContent[discovery].title}
          body={discoveryContent[discovery].body}
          actionHint={discoveryContent[discovery].actionHint}
          onDismiss={() => setDiscovery(null)}
        />
      )}
    </div>
  );
};

export default OapStep;
